import { assign, createMachine } from 'xstate';
import type { DoneInvokeEvent } from 'xstate';
import type { CustomerEmailAddresses } from '~/subscription-management/api/common/model/customer';
import type {
  BillingNotificationsContext,
  BillingNotificationsEvent,
  UpdateNotificationsEvent,
} from './billing-notifications.machine.types';
import { subscriptionApi } from '../api';
import { FORM_NAME, trackFormSubmit } from '../utils/tracking';
import { trackInvoices } from '../utils/tracking/track-invoices';

export const billingNotificationsMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./billing-notifications.machine.typegen').Typegen0,
    id: 'billing-notifications',
    schema: {
      context: {} as BillingNotificationsContext,
      events: {} as BillingNotificationsEvent,
      services: {} as {
        updateNotificationsService: { data: CustomerEmailAddresses };
        loadEmails: { data: CustomerEmailAddresses };
      },
    },
    initial: 'loading',
    states: {
      loading: {
        id: 'loadEmails',
        invoke: {
          src: 'loadEmails',
          onDone: {
            actions: [
              assign<BillingNotificationsContext, DoneInvokeEvent<CustomerEmailAddresses>>(
                (_, data) => ({
                  additionalEmailAddresses: data.data.additionalEmailAddresses,
                  error: false,
                }),
              ),
            ],
            target: 'initialise',
          },
        },
      },
      initialise: {
        always: [{ target: 'viewing', cond: 'canTargetViewing' }, { target: 'editing' }],
      },
      viewing: {
        id: 'viewing',
        on: {
          EDIT_NOTIFICATIONS: {
            target: 'editing',
          },
        },
      },
      editing: {
        id: 'editing',
        on: {
          UPDATE_NOTIFICATIONS: {
            actions: assign<BillingNotificationsContext, UpdateNotificationsEvent>(() => ({
              error: false,
            })),
            target: '#updateNotifications',
          },
        },
      },
      update: {
        id: 'updateNotifications',
        invoke: {
          id: 'updateNotificationsService',
          src: 'updateNotificationsService',
          onDone: [
            {
              target: 'initialise',
              actions: [
                'onUpdate',
                assign(
                  (
                    _: BillingNotificationsContext,
                    { data }: DoneInvokeEvent<CustomerEmailAddresses>,
                  ) => ({
                    additionalEmailAddresses: data?.additionalEmailAddresses,
                    error: false,
                  }),
                ),
                (
                  { accountId, userId }: BillingNotificationsContext,
                  { data }: DoneInvokeEvent<CustomerEmailAddresses>,
                ) => {
                  trackFormSubmit({
                    name: FORM_NAME.billingNotifications,
                    submitSuccess: true,
                    accountId,
                  });
                  trackInvoices('Billing Notification Emails Saved', {
                    accountId,
                    emailCount: data?.additionalEmailAddresses?.length ?? 0,
                    userId,
                  });
                },
              ],
            },
          ],
          onError: [
            {
              target: 'editing',
              actions: [
                assign<BillingNotificationsContext, DoneInvokeEvent<Event>>(() => ({
                  error: true,
                })),
                (context, event) =>
                  trackFormSubmit({
                    name: FORM_NAME.billingNotifications,
                    submitSuccess: false,
                    accountId: context.accountId,
                    errorMessage: event.data?.message,
                  }),
              ],
            },
          ],
        },
      },
    },
  },
  {
    services: {
      loadEmails: ({ accountId }): Promise<CustomerEmailAddresses> => {
        return subscriptionApi
          .getCustomerEmailAddresses({ accountId })
          .then(({ emailRecipients }) => ({
            additionalEmailAddresses: emailRecipients,
          }));
      },
      updateNotificationsService: (
        { accountId },
        { additionalEmailAddresses },
      ): Promise<CustomerEmailAddresses> => {
        return subscriptionApi
          .updateCustomerEmailAddresses({
            accountId,
            emailRecipients: additionalEmailAddresses,
          })
          .then(() => subscriptionApi.getCustomerEmailAddresses({ accountId }))
          .then(({ emailRecipients }) => ({
            additionalEmailAddresses: emailRecipients,
          }));
      },
    },
    guards: {
      canTargetViewing: (context): boolean =>
        context.shouldSkipViewingState !== true && context.additionalEmailAddresses?.length !== 0,
    },
  },
);
